/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import Switch from 'react-switch';
import OnCallIcon from 'components/atoms/icons/onCallIcon';
import CommentForm from 'components/molecules/commentForm';
import ActivityCheckBox from 'components/atoms/checkbox';
import ActivityComments from 'components/molecules/comments';
import ActivityStatus from 'components/atoms/activityStatus';
import common from 'constants/common';
import { addDragListener } from 'utils/dragListener';
import useUser from 'utils/useUser';
import { isCriticalOrAtRiskAlertingRole } from 'utils/accessManager/helpers';
import { isAgencyDoctor, isBiller } from 'utils/userTypeHelper';
import {
  fullNamePresenter,
  getActivityThresholdsValues,
  getLanguageLabelById,
} from 'utils';
import PatientNameHoldingStatusFlag from 'components/atoms/patientStatus/patientNameHoldingStatusFlag';
import NoteIcon from 'components/atoms/icons/noteIcon';
import ReadingItem from 'components/atoms/readingItem';

const ActivityRow = ({
  reviewed,
  activity,
  handleCommentSubmit,
  handleReviewedCheck,
  togglePatientReserved,
  setReviewedLoading,
  submitCommentLoading,
  isOnCall,
  columnNumber,
  disabledModalButton,
  openModal,
}) => {
  const { loginGuid, userType, roleNames } = useUser();
  const [commentsForm, setCommentsForm] = useState(() => {});
  const [comment, setComment] = useState(null);
  const [modalValues, setModalValues] = useState({});
  const [toggledAssign, setToggledAssign] = useState(
    loginGuid === activity.patient.guid,
  );

  const {
    connect: { handleConnectPopoutOpen, connectDetails },
    doctor: { addComment, removeComment },
  } = useDispatch();

  useEffect(() => {
    setToggledAssign(activity?.reserved?.loginGuid === loginGuid);
  }, [activity]);

  const onClickConnect = (isRpmCall) => {
    if (isCriticalOrAtRiskAlertingRole(roleNames)) {
      return;
    }
    const details = {
      phone: activity.patient.phone,
      guid: activity.patient.guid,
      isRpmCall,
      readingId: activity.id,
      deviceId: activity.deviceId,
    };
    connectDetails(details);
    handleConnectPopoutOpen(true);
    addDragListener('connect-modal', 'connect-modal-header');
  };

  const commentChanged = (value, props) => {
    setComment(value);
    setCommentsForm(props);
    if (value) addComment(activity.id);
    else removeComment(activity.id);
  };

  const commentSubmitted = (values, { resetForm }) => {
    handleCommentSubmit(activity.id, activity.deviceId, values, resetForm);
    setComment(null);
    setModalValues({});
    removeComment(activity.id);
  };

  return (
    <tr className="activity-row">
      <td data-label="№">{columnNumber}</td>
      <td data-label="Status" className="status-row">
        <ActivityStatus status={activity.globalStatus} />
      </td>
      <td data-label="Time">
        <span>
          R:{' '}
          {format(
            new Date(parseInt(activity.createdAt)),
            common.dateFormats.dayOfWeek,
          )}
        </span>
        <span>
          {format(
            new Date(parseInt(activity.createdAt)),
            common.dateFormats.timeAPM,
          )}
        </span>
      </td>
      <td data-label="Patient">
        {isCriticalOrAtRiskAlertingRole(roleNames) ? (
          <div>
            <strong>
              {activity.patient.firstName} {activity.patient.lastName}
            </strong>
          </div>
        ) : (
          <div>
            <PatientNameHoldingStatusFlag
              firstName={activity.patient.firstName}
              lastName={activity.patient.lastName}
              displayFlag={!isBiller(roleNames)}
              holdingStatus={activity.patient.holdingStatus?.status}
              dataCy="dashboard__patient-link"
              to={`/patient/${activity.patient.guid}`}
            />
          </div>
        )}
        {activity.patient.language && (
          <div>
            <strong>
              lang:&nbsp;
              {getLanguageLabelById(activity.patient.language)}
            </strong>
          </div>
        )}
        <div>{fullNamePresenter(activity.doctor)}</div>
        {isOnCall ? (
          <div className="on-call">
            {activity.patient.phone}{' '}
            <OnCallIcon fill="#2C2543" height="14" width="14" />
          </div>
        ) : (
          <div
            onClick={() => onClickConnect(true)}
            className="activity-row-number"
          >
            <strong>{activity.patient.phone}</strong>
          </div>
        )}
        {isAgencyDoctor(userType) &&
          !isCriticalOrAtRiskAlertingRole(roleNames) &&
          !reviewed && (
            <div className="activity-row-assign">
              {activity.reserved ? (
                <>
                  {activity.reserved.loginGuid === loginGuid && (
                    <>
                      <label className="switch-label">
                        <Switch
                          className="evelyn-switch"
                          onChange={() => {
                            togglePatientReserved(activity.patient.guid);
                            setToggledAssign(!toggledAssign);
                          }}
                          checked={true && toggledAssign}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          onHandleColor={common.commonColors.green}
                          offHandleColor={common.commonColors.gray}
                          onColor={common.commonColors.white}
                          offColor={common.commonColors.white}
                          handleDiameter={14}
                          height={12}
                          width={22}
                          activeBoxShadow="0"
                        />
                        <span>Unassign</span>
                      </label>
                      <span>Assigned to me</span>
                    </>
                  )}
                  {activity.reserved.loginGuid !== loginGuid && (
                    <span>{`${activity.reserved.title} ${activity.reserved.firstName} ${activity.reserved.lastName} - ${activity.reserved.professionType}`}</span>
                  )}
                </>
              ) : (
                <label className="switch-label">
                  <Switch
                    className="evelyn-switch"
                    onChange={() => {
                      togglePatientReserved(activity.patient.guid);
                      setToggledAssign(!toggledAssign);
                    }}
                    checked={false || toggledAssign}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    onHandleColor={common.commonColors.green}
                    offHandleColor={common.commonColors.gray}
                    onColor={common.commonColors.white}
                    offColor={common.commonColors.white}
                    handleDiameter={14}
                    height={12}
                    width={22}
                    activeBoxShadow="0"
                  />
                  <span>Assign</span>
                </label>
              )}
            </div>
          )}
      </td>
      <td data-label="Reading">
        <ReadingItem activity={activity} />
        {getActivityThresholdsValues(
          activity.globalStatus,
          activity.value,
          activity.statuses,
          activity.patient?.threshold,
          activity.readingType,
        ).map((threshold) => (
          <span key={threshold}>{`(${threshold})`}</span>
        ))}
      </td>
      {!isCriticalOrAtRiskAlertingRole(roleNames) && (
        <td data-label="Comment" className="rpm-comment">
          <ActivityComments
            comments={activity.comments}
            patientGuid={activity.patient.guid}
          />
          <CommentForm
            minified={activity.comments.length !== 0}
            valueChanged={commentChanged}
            handleSubmit={commentSubmitted}
            disabled={submitCommentLoading}
            activity={activity}
            comment={comment}
            hideCount
          />
          <div
            role="button"
            aria-hidden
            onClick={() =>
              openModal({
                activity,
                values: modalValues,
                setValues: setModalValues,
                handleSubmit: setComment,
              })
            }
            className="rpm-template-button"
          >
            <NoteIcon fill={disabledModalButton ? '#CCCAD7' : '#2C2543'} />
          </div>
        </td>
      )}
      {!isCriticalOrAtRiskAlertingRole(roleNames) && !reviewed && (
        <td data-label="Reviewed" className="status-row">
          <ActivityCheckBox
            handleCheck={async () => {
              await handleReviewedCheck(activity.id, activity.deviceId);
              if (comment && commentsForm) {
                await handleCommentSubmit(
                  activity.id,
                  activity.deviceId,
                  { comment },
                  commentsForm.handleReset,
                );
                setComment(null);
              }
            }}
            loading={setReviewedLoading || activity.disabled || false}
          />
        </td>
      )}
    </tr>
  );
};

ActivityRow.propTypes = {
  columnNumber: PropTypes.number,
  reviewed: PropTypes.bool.isRequired,
  activity: PropTypes.shape().isRequired,
  handleCommentSubmit: PropTypes.func.isRequired,
  handleReviewedCheck: PropTypes.func.isRequired,
  togglePatientReserved: PropTypes.func.isRequired,
  submitCommentLoading: PropTypes.bool.isRequired,
  setReviewedLoading: PropTypes.bool.isRequired,
  isOnCall: PropTypes.bool,
  disabledModalButton: PropTypes.bool,
  openModal: PropTypes.func,
};

export default ActivityRow;
